<template>
    <div class="doc-editor" :class="{ 'doc-editor--page': pageMode, 'doc-editor--fullscreen': fullscreen, 'doc-editor--disabled': disabled }"
        @keydown.esc="onEscape">
        <div v-if="fullscreen && title" class="doc-editor__titlebar">
            <span class="doc-editor__title">{{ title }}</span>
            <span v-if="status" class="doc-editor__status-text">{{ status }}</span>
            <span class="doc-editor__spacer"></span>
            <slot name="actions"></slot>
            <v-btn icon small aria-label="Sair da tela cheia" @click="fullscreen = false"><v-icon size="20">mdi-close</v-icon></v-btn>
        </div>

        <editor-toolbar v-if="editor && !disabled" :editor="editor" :tick="tick" :page-mode="pageMode" :fullscreen="fullscreen"
            :search-open="search.open" :painter-armed="!!painter" :zoom="zoom" :margin-key="marginKey" :orientation="pageOrientation" :can-fullscreen="!noFullscreen"
            :styles-open="stylesOpen" @toggle-styles="stylesOpen = !stylesOpen"
            @toggle-painter="togglePainter" @toggle-search="toggleSearch" @toggle-page="pageMode = !pageMode"
            @toggle-fullscreen="fullscreen = !fullscreen" @zoom="zoom = $event" @margins="setMargins" @orientation="pageOrientation = $event"
            @link="editLink" @footnote="editFootnote" @image="$refs.imageInput.click()" @attachment="$refs.attachmentInput.click()">
            <template v-if="$slots.insert" #insert><slot name="insert"></slot></template>
            <template v-if="$slots.toolbar" #extra><slot name="toolbar"></slot></template>
        </editor-toolbar>

        <div v-if="search.open" class="doc-editor__search">
            <v-icon small class="ui-muted">mdi-magnify</v-icon>
            <input ref="searchInput" v-model="search.term" class="doc-editor__search-input" placeholder="Localizar" aria-label="Localizar"
                @input="runSearch" @keydown.enter.prevent="$event.shiftKey ? editor.commands.previousSearchResult() : editor.commands.nextSearchResult()" />
            <span class="doc-editor__search-count">{{ searchCountText }}</span>
            <v-btn icon x-small aria-label="Anterior" @click="editor.commands.previousSearchResult()"><v-icon small>mdi-chevron-up</v-icon></v-btn>
            <v-btn icon x-small aria-label="Próximo" @click="editor.commands.nextSearchResult()"><v-icon small>mdi-chevron-down</v-icon></v-btn>
            <span class="doc-editor__search-sep"></span>
            <input v-model="search.replacement" class="doc-editor__search-input" placeholder="Substituir por" aria-label="Substituir por" />
            <v-btn small text :disabled="!searchCount" @click="editor.commands.replaceCurrent(search.replacement)">Substituir</v-btn>
            <v-btn small text :disabled="!searchCount" @click="editor.commands.replaceAll(search.replacement)">Substituir tudo</v-btn>
            <v-btn icon x-small aria-label="Fechar busca" @click="toggleSearch"><v-icon small>mdi-close</v-icon></v-btn>
        </div>

        <div class="doc-editor__body">
            <div ref="canvas" class="doc-editor__canvas" :style="canvasStyle">
                <editor-ruler v-if="pageMode && editor && !disabled" class="doc-editor__ruler" :zoom="zoom" :page-width-cm="pageSize.widthCm" :margin-left="margins.left" :margin-right="margins.right"
                    :left-cm="currentIndent.left" :first-line-cm="currentIndent.firstLine" @change="onRulerChange" />
                <div class="doc-editor__sheet" :style="sheetStyle">
                    <editor-content :editor="editor" class="doc-editor__content" />
                    <!-- Folha em paginas inteiras: linha tracejada na troca de pagina e o
                         rodape do timbrado em cada uma (so visual; a quebra real e do PDF). -->
                    <template v-if="pageMode">
                        <div v-for="n in pageCount - 1" :key="`edge-${n}`" class="doc-editor__page-edge" :style="{ top: `${n * pageHeightPx}px` }"></div>
                        <div v-for="n in pageCount" :key="`footer-${n}`" class="doc-editor__page-footer" :style="pageFooterStyle(n)">
                            <span>{{ footerLabel }}</span>
                            <span>{{ n }} / {{ pageCount }}</span>
                        </div>
                    </template>
                </div>
            </div>
            <editor-styles-panel v-if="stylesOpen && editor && !disabled" :editor="editor" :tick="tick" @close="stylesOpen = false" />
        </div>

        <div class="doc-editor__statusbar">
            <span>{{ stats.words }} {{ stats.words === 1 ? "palavra" : "palavras" }}</span>
            <span>{{ stats.characters }} caracteres</span>
            <span v-if="pageMode">{{ pagesText }}</span>
            <span v-if="painter" class="doc-editor__hint">Selecione o texto que vai receber a formatação</span>
            <span class="doc-editor__spacer"></span>
            <span v-if="status && !(fullscreen && title)">{{ status }}</span>
        </div>

        <input ref="imageInput" type="file" accept="image/png,image/jpeg,image/webp,image/gif" class="d-none" @change="onImageSelected" />
        <input ref="attachmentInput" type="file" multiple class="d-none" @change="onAttachmentsSelected" />
    </div>
</template>

<script>
import { createDocumentEditor, EditorContent } from "./editor-core";
import EditorToolbar from "./EditorToolbar.vue";
import EditorRuler from "./EditorRuler.vue";
import EditorStylesPanel from "./EditorStylesPanel.vue";
import { loadStyles } from "./styles/style-store";
import { toCm } from "./extensions/formatting";
import { cleanPastedHtml } from "./extensions/search";
import { CM_TO_PX, PAGE, PAGE_SIZES, PAGE_FOOTER, MARGIN_PRESETS } from "./brand";
import "./document-editor.scss";

const MARGIN_STORAGE_KEY = "bej.editor.margins";
const STYLES_STORAGE_KEY = "bej.editor.stylesPanel";

function readPreference(key, fallback) {
    try {
        return localStorage.getItem(key) || fallback;
    } catch (error) {
        return fallback;
    }
}

function writePreference(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch (error) {
        // preferencia so nao fica salva
    }
}

function escapeHtml(value) {
    return String(value || "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[char]));
}

function readAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

// Editor de documentos do backoffice (Tiptap 2). v-model com HTML; o HTML
// gerado traz a formatacao em linha para sair igual no PDF, no Word e no
// e-mail. Modos: texto corrido (padrao) ou folha A4 com regua e margens.
export default {
    name: "UiDocumentEditor",

    components: { EditorContent, EditorToolbar, EditorRuler, EditorStylesPanel },

    props: {
        value: { type: String, default: "" },
        placeholder: { type: String, default: "Escreva o texto..." },
        disabled: { type: Boolean, default: false },
        // Abre como folha A4 (regua, margens e zoom).
        page: { type: Boolean, default: false },
        // Orientacao da folha: "portrait" (21 x 29,7) ou "landscape" (29,7 x 21, como o RVC).
        orientation: { type: String, default: "portrait", validator: (value) => !!PAGE_SIZES[value] },
        // Altura minima do texto corrido, em px.
        minHeight: { type: [Number, String], default: 220 },
        // Altura maxima do texto corrido antes de rolar (null = cresce).
        maxHeight: { type: [Number, String], default: null },
        // Titulo e status mostrados na tela cheia (ex.: "Motivo", "Salvo ha 1 min").
        title: { type: String, default: null },
        status: { type: String, default: null },
        // Esconde o botao de tela cheia (quando a tela ja tem o seu "expandir").
        noFullscreen: { type: Boolean, default: false },
    },

    data() {
        return {
            editor: null,
            tick: 0,
            pageMode: this.page,
            pageOrientation: this.orientation,
            fullscreen: false,
            zoom: 1,
            marginKey: readPreference(MARGIN_STORAGE_KEY, "bej"),
            stylesOpen: readPreference(STYLES_STORAGE_KEY, "0") === "1",
            search: { open: false, term: "", replacement: "" },
            painter: null,
            contentHeight: 0,
        };
    },

    computed: {
        margins() {
            return MARGIN_PRESETS.find((preset) => preset.key === this.marginKey) || MARGIN_PRESETS[0];
        },
        pageSize() {
            return PAGE_SIZES[this.pageOrientation] || PAGE;
        },
        pageHeightPx() {
            return this.pageSize.heightCm * CM_TO_PX * this.zoom;
        },
        pageCount() {
            const pageTextHeight = (this.pageSize.heightCm - this.margins.top - this.margins.bottom) * CM_TO_PX * this.zoom;
            return Math.max(1, Math.ceil(this.contentHeight / pageTextHeight));
        },
        footerLabel() {
            return PAGE_FOOTER.label;
        },
        canvasStyle() {
            if (this.pageMode) return {};
            return {
                minHeight: `${this.minHeight}px`,
                maxHeight: this.maxHeight && !this.fullscreen ? `${this.maxHeight}px` : undefined,
            };
        },
        sheetStyle() {
            if (!this.pageMode) return {};
            const scale = CM_TO_PX * this.zoom;
            return {
                width: `${this.pageSize.widthCm * scale}px`,
                minHeight: `${this.pageCount * this.pageSize.heightCm * scale}px`,
                padding: `${this.margins.top * scale}px ${this.margins.right * scale}px ${this.margins.bottom * scale}px ${this.margins.left * scale}px`,
                fontSize: `${this.zoom * 100}%`,
                "--doc-zoom": this.zoom,
            };
        },
        stats() {
            void this.tick;
            if (!this.editor) return { words: 0, characters: 0 };
            return { words: this.editor.storage.characterCount.words(), characters: this.editor.storage.characterCount.characters() };
        },
        pagesText() {
            const pages = this.pageCount;
            return `${pages} ${pages === 1 ? "página" : "páginas"} (estimado)`;
        },
        currentIndent() {
            void this.tick;
            if (!this.editor) return { left: 0, firstLine: 0 };
            const type = this.editor.isActive("heading") ? "heading" : "paragraph";
            const attrs = this.editor.getAttributes(type);
            return { left: toCm(attrs.indentLeft), firstLine: toCm(attrs.textIndent) };
        },
        searchCount() {
            void this.tick;
            return this.editor ? this.editor.storage.documentSearch.results.length : 0;
        },
        searchCountText() {
            if (!this.search.term) return "";
            if (!this.searchCount) return "Nenhum resultado";
            return `${this.editor.storage.documentSearch.current + 1} de ${this.searchCount}`;
        },
    },

    watch: {
        value(html) {
            if (!this.editor) return;
            if (html === this.currentHtml()) return;
            this.editor.commands.setContent(html || "", false);
        },
        disabled(value) {
            if (this.editor) this.editor.setEditable(!value);
        },
        orientation(value) {
            this.pageOrientation = value;
        },
        // A altura do texto muda com a folha; a contagem de paginas precisa acompanhar.
        pageMode() {
            this.measure();
        },
        pageOrientation() {
            this.measure();
        },
        zoom() {
            this.measure();
        },
        fullscreen(value) {
            if (value) this.pageMode = true;
            document.documentElement.classList.toggle("doc-editor-lock", value);
            this.$nextTick(() => this.editor && this.editor.commands.focus());
        },
        marginKey(value) {
            writePreference(MARGIN_STORAGE_KEY, value);
            this.measure();
        },
        stylesOpen(value) {
            writePreference(STYLES_STORAGE_KEY, value ? "1" : "0");
        },
    },

    mounted() {
        loadStyles();
        this.editor = createDocumentEditor({
            content: this.value || "",
            editable: !this.disabled,
            placeholder: this.placeholder,
            editorProps: {
                attributes: { spellcheck: "true", lang: "pt-BR", "aria-label": this.title || "Editor de texto" },
                transformPastedHTML: (html) => cleanPastedHtml(html),
                handlePaste: (view, event) => this.insertFilesFromEvent(event.clipboardData),
                handleDrop: (view, event) => this.insertFilesFromEvent(event.dataTransfer),
                // Duplo clique no numero da nota abre o texto dela.
                handleDoubleClickOn: (view, pos, node) => {
                    if (node.type.name !== "footnote") return false;
                    this.editor.commands.setNodeSelection(pos);
                    this.editFootnote();
                    return true;
                },
                handleKeyDown: (view, event) => {
                    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "f") {
                        event.preventDefault();
                        this.openSearch();
                        return true;
                    }
                    return false;
                },
            },
            onUpdate: () => {
                this.$emit("input", this.currentHtml());
            },
            onTransaction: () => {
                this.tick += 1;
                this.measure();
            },
            onSelectionUpdate: () => this.applyPainter(),
            onBlur: () => this.$emit("blur"),
        });
    },

    beforeDestroy() {
        document.documentElement.classList.remove("doc-editor-lock");
        if (this.editor) this.editor.destroy();
    },

    methods: {
        // HTML vazio vira "" para as validacoes de "motivo obrigatorio".
        currentHtml() {
            if (!this.editor || this.editor.isEmpty) return "";
            return this.editor.getHTML();
        },
        focus() {
            if (this.editor) this.editor.commands.focus();
        },
        // Insere HTML onde esta o cursor (ex.: textos automaticos).
        insertHtml(html) {
            if (this.editor) this.editor.chain().focus().insertContent(html).run();
        },
        measure() {
            if (!this.pageMode || !this.$el) return;
            this.$nextTick(() => {
                // Mede ate o fim do ultimo bloco: o .ProseMirror estica para preencher a
                // folha, entao o scrollHeight dele nunca diminuiria depois de crescer.
                const content = this.$el.querySelector(".ProseMirror");
                const last = content && content.lastElementChild;
                this.contentHeight = last ? last.getBoundingClientRect().bottom - content.getBoundingClientRect().top : 0;
            });
        },
        setMargins(key) {
            this.marginKey = key;
        },
        pageFooterStyle(page) {
            const scale = CM_TO_PX * this.zoom;
            return {
                top: `${page * this.pageHeightPx - (this.margins.bottom - PAGE_FOOTER.offsetCm) * scale}px`,
                left: `${PAGE_FOOTER.sideCm * scale}px`,
                right: `${PAGE_FOOTER.sideCm * scale}px`,
                fontSize: `${(PAGE_FOOTER.fontPt * 4 / 3) * this.zoom}px`,
            };
        },
        onEscape() {
            if (this.search.open) this.toggleSearch();
            else if (this.fullscreen) this.fullscreen = false;
        },
        onRulerChange({ left, firstLine }) {
            this.editor.chain().focus().setIndents({ left, firstLine }).run();
        },

        // Localizar e substituir
        openSearch() {
            this.search.open = true;
            this.$nextTick(() => this.$refs.searchInput && this.$refs.searchInput.focus());
        },
        toggleSearch() {
            if (this.search.open) {
                this.search.open = false;
                this.editor.commands.setSearchTerm("");
                this.editor.commands.focus();
            } else {
                this.openSearch();
            }
        },
        runSearch() {
            this.editor.commands.setSearchTerm(this.search.term);
        },

        // Pincel: guarda as marcas e o formato do paragrafo do trecho atual e
        // aplica no proximo texto selecionado.
        togglePainter() {
            if (this.painter) {
                this.painter = null;
                return;
            }
            const { state } = this.editor;
            const marks = (state.storedMarks || state.selection.$from.marks()).map((mark) => ({ type: mark.type.name, attrs: { ...mark.attrs } }));
            const block = state.selection.$from.parent;
            this.painter = { marks, block: { textAlign: block.attrs.textAlign, lineHeight: block.attrs.lineHeight, indentLeft: block.attrs.indentLeft, textIndent: block.attrs.textIndent } };
        },
        applyPainter() {
            if (!this.painter || this.editor.state.selection.empty) return;
            const { marks, block } = this.painter;
            this.painter = null;
            let chain = this.editor.chain().unsetAllMarks();
            marks.forEach((mark) => { chain = chain.setMark(mark.type, mark.attrs); });
            if (block.textAlign) chain = chain.setTextAlign(block.textAlign);
            chain = chain.setLineHeight(block.lineHeight || null).setIndents({ left: toCm(block.indentLeft), firstLine: toCm(block.textIndent) });
            chain.run();
        },

        // Link, imagem e anexos
        editLink() {
            const previous = this.editor.getAttributes("link").href || "";
            const url = window.prompt("Endereço do link (deixe vazio para remover):", previous);
            if (url === null) return;
            if (!url.trim()) {
                this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
                return;
            }
            const href = /^(https?:|mailto:|tel:)/i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
            this.editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
        },
        // Nota de rodape: com o numero de uma nota selecionado, edita (ou remove,
        // se o texto ficar vazio); senao insere uma nota nova no cursor.
        editFootnote() {
            const { node } = this.editor.state.selection;
            const current = node && node.type.name === "footnote" ? node.attrs.text : null;
            const text = window.prompt(current === null ? "Texto da nota de rodapé:" : "Texto da nota de rodapé (deixe vazio para remover):", current || "");
            if (text === null) return;
            if (text.trim()) this.editor.chain().focus().setFootnote(text).run();
            else if (current !== null) this.editor.chain().focus().unsetFootnote().run();
        },
        async onImageSelected(event) {
            const [file] = event.target.files || [];
            event.target.value = "";
            if (file) await this.insertImageFile(file);
        },
        async onAttachmentsSelected(event) {
            const files = [...(event.target.files || [])];
            event.target.value = "";
            for (const file of files) await this.insertAttachment(file);
        },
        async insertImageFile(file) {
            const src = await readAsDataUrl(file);
            this.editor.chain().focus().setImage({ src, alt: file.name }).run();
        },
        async insertAttachment(file) {
            const href = await readAsDataUrl(file);
            const name = escapeHtml(file.name);
            this.editor.chain().focus().insertContent(`<p><a href="${href}" download="${name}" target="_blank">Anexo: ${name}</a></p>`).run();
        },
        insertFilesFromEvent(transfer) {
            const files = transfer && transfer.files ? [...transfer.files] : [];
            if (!files.length) return false;
            files.forEach((file) => {
                if (file.type.startsWith("image/")) this.insertImageFile(file);
                else this.insertAttachment(file);
            });
            return true;
        },
    },
};
</script>
